import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IFormCanDeactivateGuard } from 'src/app/data/guard/form-can-deactivate.guard';
import { ClassroomFilterDto } from 'src/app/data/models/dto/classroom-filter.dto';
import { FileDto } from 'src/app/data/models/dto/file.dto';
import { GenericDialogDto } from 'src/app/data/models/dto/generic-dialog.dto';
import { SnackbarTypeEnum } from 'src/app/data/models/enum/snackbar-type.enum';
import { News } from 'src/app/data/models/interfaces/news';
import { ClassRoomsService } from 'src/app/data/services/classroom.service';
import { FileService } from 'src/app/data/services/file.service';
import { NewsService } from 'src/app/data/services/news.service';
import { ProfileService } from 'src/app/data/services/profile.service';
import { isGreaterThanToday, isToday } from 'src/app/data/utils/date.utils';
import { genericDialog } from 'src/app/data/utils/generic-dialog.utils';
import {
  ExcludeProfessorAndEmployees,
  formatSelectedProfilesText,
  MANAGEMENT_ADVISOR_PROFILES,
  RESTRICTED_ROUTE_PROFILES,
} from 'src/app/data/utils/profile.utils';
import { UserUtil } from 'src/app/data/utils/user.utils';
import { CustomSnackbarService } from 'src/app/ui/components/custom-snackbar/custom-snackbar-service';
import { FlatNode } from 'src/app/ui/components/select-hierarquical-list/model/flat.node.interface';
import { SimpleNode } from 'src/app/ui/components/select-hierarquical-list/model/simple.node.interface';
import { ImageItem } from 'src/app/ui/components/select-simple-list/model/item-image.interface';

const moment = require('moment');

@Component({
  selector: 'app-news',
  templateUrl: './news.component.html',
  styleUrls: ['./news.component.scss'],
})
export class NewsComponent
  implements OnInit, OnDestroy, IFormCanDeactivateGuard
{
  profilesElegibleToManageNews = RESTRICTED_ROUTE_PROFILES(MANAGEMENT_ADVISOR_PROFILES);
  //NEWS DATA
  isUpdate: boolean = false;
  newsId: string | undefined;
  news: News | undefined;

  iconDoubtsPath = '../../../../assets/icons/BlueIconDoubts.svg';
  isAuthorizationOn = false;

  backgroundFilePath = '';
  attachmentsPath = '';

  classroomsList: string[] = [];

  hasClassrooms: boolean = true;
  alertMessage = 'Esta notícia não poderá ser criada pois ainda não existem turmas cadastradas para o ano letivo selecionado.';

  newsSubscription: Subscription = new Subscription();

  //MESSAGES
  successSaveMessage = '';
  errorSaveMessage = '';
  succcessDeleteMessage = '';
  errorDeleteMessage = '';
  discardMessage = '';

  profilesPlaceholder = '';
  profilesSelectAllText = 'Todos os perfis';
  selectedIdsProfiles: string[] = [];
  startTxtYear = '';
  segmentTreeData: SimpleNode[] = [];
  selectedIdsClassrooms: string[] = [];

  newsForm: FormGroup = this.fb.group(
    {
      profiles: new FormControl({ value: '', disabled: false }, [
        Validators.required,
      ]),
      classrooms: new FormControl({ value: '', disabled: false }, [
        Validators.required,
      ]),
      title: new FormControl({ value: '', disabled: false }, [
        Validators.required,
      ]),
      description: new FormControl({ value: '', disabled: false }, [
        Validators.required,
      ]),
      releaseDate: new FormControl({ value: '', disabled: false }),
      releaseTime: new FormControl({ value: '', disabled: false }),
      authorizationLimitDate: new FormControl({ value: '', disabled: false }),
      authorizationLimitTime: new FormControl({ value: '', disabled: false }),
    },
    {
      validators: [
        this.releaseDateIsGreaterThanToday(),
        this.releaseTimeIsGreaterThanNow(),
        this.validateAuthorizationLimitTime(),
      ],
    }
  );

  mobile: boolean = false;
  minDate = new Date();

  @HostListener('window:resize', ['$event'])
  isMobile() {
    if (window.screen.width < 768) {
      this.mobile = true;
    } else {
      this.mobile = false;
    }
  }

  excludeProfiles = ExcludeProfessorAndEmployees;

  constructor(
    private dialog: MatDialog,
    private service: NewsService,
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private snackbarService: CustomSnackbarService,
    private fileService: FileService,
    private classroomsService: ClassRoomsService,
    private profileService: ProfileService,
    private readonly userUtil: UserUtil
  ) {
    this.route.params.subscribe((params: Params) => {
      this.newsId = params['id'];
      if (this.newsId) {
        this.isUpdate = true;
      }
    });
  }

  async canLeaveForm(): Promise<boolean> {
    const dialogInfo: GenericDialogDto = {
      imageUrl: 'assets/images/discard-changes-news-form.svg',
      primaryText: this.isUpdate
        ? 'Você tem certeza de que deseja descartar as alterações?'
        : 'Você tem certeza de que deseja descartar as informações?',
      secondaryText: this.isUpdate
        ? 'Se você sair sem salvar, suas alterações não serão aplicadas.'
        : 'Se você sair sem criar ou programar a notícia, as informações inseridas não serão salvas.',
      btnCancelText: 'Não, cancelar',
      btnConfirmText: 'Sim, descartar',
    };
    if (this.newsForm.dirty && !this.saveInProgress) {
      const confirmation = await genericDialog(
        this.dialog,
        dialogInfo,
        'custom-dialog-confirmation'
      ).toPromise();
      if (confirmation)
        this.snackbarService.open(
          this.discardMessage,
          SnackbarTypeEnum.SUCCESS,
          'center',
          'bottom'
        );
      return confirmation;
    } else {
      return true;
    }
  }

  attachments: any[] = [];
  attachmentsToDelete: FileDto[] = [];
  currentAttachments: any[] = [];
  onCurrentAttachmentsEmit(emit: FileDto[]) {
    this.currentAttachments = emit;
    this.newsForm.markAsDirty();
  }
  onAttachmentsToDeleteEmit(emit: FileDto[]) {
    this.attachmentsToDelete = emit;
    this.newsForm.markAsDirty();
  }

  bgFiles: any[] = [];
  bgFilesToDelete: FileDto[] = [];
  currentBgFiles: any[] = [];
  onCurrentBackgroundFileEmit(emit: FileDto[]) {
    this.currentBgFiles = emit;
    this.newsForm.markAsDirty();
  }
  onBackgroundFileToDeleteEmit(emit: FileDto[]) {
    this.bgFilesToDelete.push(...emit);
    this.newsForm.markAsDirty();
  }

  private get schools() {
    return this.userUtil.getSchools();
  }

  ngOnDestroy(): void {
    this.newsSubscription.unsubscribe();
  }

  async ngOnInit(): Promise<void> {
    this.discardMessage = 'As informações foram descartadas';
    if (this.newsId) {
      this.discardMessage = 'As alterações foram descartadas';
      this.news = await this.getNews(this.newsId);
      this.selectedIdsProfiles = this.news?.idsProfiles!;
      this.selectedIdsClassrooms = this.news?.idsClassRooms!;
      this.startTxtYear = this.news?.year!;
      if (
        this.news.backgroundFile &&
        typeof this.news.backgroundFile === 'string'
      )
        this.backgroundFilePath = `news/background/${this.newsId}`;
      if (
        this.news.files &&
        this.news.files.length > 0 &&
        typeof this.news.files[0] === 'string'
      )
        this.attachmentsPath = `news/${this.newsId}`;
    } else {
      this.startTxtYear = new Date().getFullYear().toString();
      this.initNews();
      this.selectedIdsProfiles = [];
    }

    const filter: ClassroomFilterDto = {
      year: this.startTxtYear,
      idSchool: this.schools[0].id,
    };
    this.loadSegmentTree(filter);
    this.initData();
    this.loadChangesForm();
    this.editNewsReleased(this.newsId);
    if (this.newsForm.get('releaseDate')?.hasError('dates')) {
      this.newsForm.get('releaseDate')?.markAsTouched();
    }
    if (this.newsForm.get('releaseTime')?.hasError('time')) {
      this.newsForm.get('releaseTime')?.markAsTouched();
    }
    await this.loadFormProfiles();
  }

  private async loadFormProfiles() {
    if (this.newsId) {
      const result = await this.profileService.getAll().toPromise();

      const profiles = result.map(
        (profile) =>
          <ImageItem>{
            id: profile.id,
            imageUrl: '',
            name: profile.name,
            isChecked: this.selectedIdsProfiles.includes(profile.id!),
          }
      );

      const profileNames = formatSelectedProfilesText(profiles);
      this.newsForm.controls['profiles'].setValue(profileNames);
    }
  }

  loadChangesForm() {
    this.newsForm.valueChanges.subscribe(() => {
      this.defineMessageText();
    });

    this.newsForm.get('releaseDate')?.valueChanges.subscribe((val) => {
      if (val) {
        if (!this.newsForm.get('releaseTime')?.value) {
          this.newsForm.get('releaseTime')?.setValue('23:59');
        }
        this.newsForm.get('releaseTime')?.setValidators(Validators.required);
      } else {
        this.newsForm.get('releaseTime')?.clearValidators();
      }
      this.newsForm.get('releaseTime')?.updateValueAndValidity();
    });

    this.newsForm
      .get('authorizationLimitDate')
      ?.valueChanges.subscribe((val) => {
        if (val) {
          if (!this.newsForm.get('authorizationLimitTime')?.value) {
            this.newsForm.get('authorizationLimitTime')?.setValue('23:59');
          }
          this.newsForm
            .get('authorizationLimitTime')
            ?.setValidators(Validators.required);
        } else {
          this.newsForm.get('authorizationLimitTime')?.clearValidators();
        }
        this.newsForm.get('authorizationLimitTime')?.updateValueAndValidity();
      });
  }

  private initNews(): void {
    this.news = <News>{
      id: undefined,
      title: '',
      description: '',
      year: new Date().getFullYear().toString(),
      location: '',
      confirmationDate: undefined,
      type: 'News',
      active: true,
      backgroundFile: undefined,
      files: [],
      idsProfiles: [],
      idsClassRooms: [],
      idSchool: '',
    };
  }

  private initData(): void {
    this.loadForm(this.news);
    this.loadFiles();
  }

  async getNews(id: string): Promise<News> {
    return await this.service.getById(id).toPromise();
  }

  loadFiles() {
    this.bgFiles = this.news?.backgroundFile ? [this.news?.backgroundFile] : [];
    this.currentBgFiles = this.bgFiles;

    this.attachments = this.news?.files ?? [];
    this.currentAttachments = this.attachments;
  }

  private loadReleaseDate(date?: Date) {
    if (date) {
      var dtMoment = moment(date).utc(true);
      var dt = dtMoment.toDate();
      return {
        releaseDate: dt,
        releaseTime: dtMoment.format('HH:mm'),
      };
    }
    return undefined;
  }

  private loadAuthorizationLimitDate(date?: Date | null) {
    if (date) {
      var dtMoment = moment(date).utc(true);
      var dt = dtMoment.toDate();
      return {
        authorizationLimitDate: dt,
        authorizationLimitTime: dtMoment.format('HH:mm'),
      };
    }
    return undefined;
  }

  loadForm(news: News | undefined) {
    if (news) {
      var releaseDate = this.loadReleaseDate(news.releaseDate);
      var authorizationLimitDate = this.loadAuthorizationLimitDate(
        news.authorizationLimit
      );
      this.isAuthorizationOn = news.hasAuthorization ?? false;

      this.newsForm.reset({
        title: news.title,
        description: news.description,
        ...releaseDate,
        ...authorizationLimitDate,
      });
    }
  }

  private saveNews() {
    const uniqueClassRoomsSet = new Set(this.classroomsList);
    this.classroomsList = [...uniqueClassRoomsSet];

    const news = this.createNews(this.currentBgFiles, this.currentAttachments);

    if (this.isUpdate) {
      this.successSaveMessage = 'Notícia salva com sucesso';
      this.errorSaveMessage = 'Erro ao salvar notícia';
    } else {
      if (this.newsForm.value.releaseDate && this.newsForm.value.releaseTime) {
        this.successSaveMessage = 'Notícia programada com sucesso';
        this.errorSaveMessage = 'Erro ao programar notícia';
      } else {
        this.successSaveMessage = 'Notícia criada com sucesso';
        this.errorSaveMessage = 'Erro ao criar notícia';
      }
    }
    if (this.isUpdate) this.update(news);
    else this.save(news);

    this.bgFilesToDelete.forEach((f) => {
      this.fileService.delete(f.id!).toPromise();
    });
    this.attachmentsToDelete.forEach((f) => {
      this.fileService.delete(f.id!).toPromise();
    });
  }

  private buildReleaseDate() {
    var rDate: Date | undefined;
    if (this.newsForm?.value.releaseDate) {
      rDate = moment(this.newsForm.value.releaseDate).utc(false).toDate();
    }
    if (this.newsForm?.value.releaseTime) {
      var [hour, min] = this.newsForm.value.releaseTime.split(':');
      if (rDate) {
        rDate.setHours(hour);
        rDate.setMinutes(min);
      }
    }
    return rDate;
  }

  private buildAuthorizationLimitDate() {
    var rDate: Date | null = null;
    if (this.newsForm?.value.authorizationLimitDate) {
      rDate = moment(this.newsForm.value.authorizationLimitDate)
        .utc(false)
        .toDate();
    }
    if (this.newsForm?.value.authorizationLimitTime) {
      var [hour, min] = this.newsForm.value.authorizationLimitTime.split(':');
      if (rDate) {
        rDate.setHours(hour);
        rDate.setMinutes(min);
      }
    }
    return rDate;
  }

  private save(news: News) {
    this.service
      .save(news)
      .toPromise()
      .then(
        (res) => {
          this.snackbarService.open(
            this.successSaveMessage,
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          this.router.navigate(['news-list']);
        },
        (error) => {
          console.log(error);
          this.snackbarService.open(
            this.errorSaveMessage,
            SnackbarTypeEnum.ERROR,
            'center',
            'bottom'
          );
        }
      );
  }

  private update(news: News) {
    this.service
      .update(news)
      .toPromise()
      .then(
        (res) => {
          this.snackbarService.open(
            this.successSaveMessage,
            SnackbarTypeEnum.SUCCESS,
            'center',
            'bottom'
          );
          this.router.navigate(['news-list']);
        },
        (error) => {
          console.log(error);
          this.snackbarService.open(
            this.errorSaveMessage,
            SnackbarTypeEnum.ERROR,
            'center',
            'bottom'
          );
        }
      );
  }

  private createNews(
    backgroundFile: FileDto[] | string[] | undefined,
    attachFiles: FileDto[] | string[] | undefined
  ): News {
    let background: string | FileDto | null = null;
    if (backgroundFile && backgroundFile.length > 0)
      background = backgroundFile[0];
    const authorizationLimit = this.buildAuthorizationLimitDate();
    const news: News = {
      id: this.news?.id,
      title: this.newsForm.value.title,
      description: this.newsForm.value.description,
      year: this.news?.year
        ? this.news?.year
        : new Date().getFullYear().toString(),
      location: '',
      confirmationDate: undefined,
      type: 'News',
      active: true,
      backgroundFile: background,
      files: attachFiles,
      idsProfiles: this.selectedIdsProfiles,
      idsClassRooms: this.selectedIdsClassrooms,
      releaseDate: this.buildReleaseDate(),
      idSchool: this.schools[0].id,
      hasAuthorization: authorizationLimit ? true : false,
      authorizationLimit: authorizationLimit,
    };
    return news;
  }

  onCancelClicked() {
    this.router.navigate(['news-list']);
  }

  private async loadSegmentTree(filter: ClassroomFilterDto): Promise<void> {
    try {
      const tree = await this.classroomsService.getTree(filter).toPromise();
      this.segmentTreeData = tree.map((seg) => ({
        id: seg.id,
        name: seg.name,
        children: seg.series.map((ser) => ({
          name: ser.name,
          children: ser.classRooms.map((cls) => ({
            id: cls.id,
            name: cls.name,
          })),
        })),
      }));
      let classrooms: string[] = [];
      tree.forEach((node) =>
        node.series.forEach((serie) =>
          serie.classRooms.forEach((classroom) => {
            if (this.news?.idsClassRooms?.includes(classroom.id)) {
              classrooms.push(classroom.name);
            }
          })
        )
      );
      this.newsForm.controls['classrooms'].setValue(
        this.formatSelectedClassroomsText(classrooms)
      );
    } catch (error: any) {
      this.hasClassrooms = false;

      if (error.status === 404) {
        this.segmentTreeData = [];

        this.updateClassroomsControlState();
        this.alertMessage;
      } else {
        console.error(error);
      }
    }

  }

  private updateClassroomsControlState(classrooms?: string[]): void {
    const control = this.newsForm.controls['classrooms']

    if (!this.hasClassrooms) {
      control?.setValue('');
      control?.disable({ emitEvent: false });
    } else {
      control?.enable({ emitEvent: false });
      control?.setValue(
        this.formatSelectedClassroomsText(classrooms ?? [])
      );
    }
  }

  openList(evt: any, trigger: MatAutocompleteTrigger): void {
    evt.stopPropagation();
    if (trigger.panelOpen) {
      trigger.closePanel();
    } else {
      trigger.openPanel();
    }
  }

  handleProfileSelect(profiles: ImageItem[]): void {
    const selectedProfiles = profiles.filter((item) => item.isChecked);
    const ids = selectedProfiles.map((evt) => evt.id);
    this.selectedIdsProfiles = ids as string[];
    const selectedProfilesText = formatSelectedProfilesText(profiles);
    this.newsForm.controls['profiles'].setValue(selectedProfilesText);
    this.newsForm.markAsDirty();
  }

  async getYearSelected(year: string) {
    this.news!.year = year;
    this.newsForm.controls['classrooms'].setValue('');
    this.segmentTreeData = [];

    const filter: ClassroomFilterDto = {
      year: year,
      idSchool: this.schools[0].id,
    };
    await this.loadSegmentTree(filter);
  }

  handleSelectClassroom(classrooms: FlatNode[]) {
    const ids = classrooms.map((classroom) => classroom.id);
    this.selectedIdsClassrooms = ids;
    const names = classrooms.map((classroom) => classroom.name);
    this.newsForm.controls['classrooms'].setValue(
      this.formatSelectedClassroomsText(names)
    );
    this.newsForm.markAsDirty();
  }

  private formatSelectedClassroomsText(classrooms: string[]): string {
    let classroomsText = '';
    if (classrooms.length >= 1) {
      classroomsText = classrooms[0];
      if (classrooms.length > 1) {
        classroomsText = classroomsText
          .concat(' +')
          .concat((classrooms.length - 1).toString());
      }
    }
    return classroomsText;
  }

  private releaseDateIsGreaterThanToday(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let d = this.buildReleaseDate();
      let err = null;

      if (d && !isNaN(d.getTime()) && !isGreaterThanToday(d)) {
        err = { dates: 'Date must be greater than today' };
      }
      control.get('releaseDate')?.setErrors(err);
      return err;
    };
  }

  inProgressText = '';
  saveButtonText = 'estadoIni';
  saveInProgress = false;
  defineMessageText(): void {
    if (this.isUpdate) {
      this.inProgressText = 'Salvando';
      this.saveButtonText = 'Salvar';
    } else {
      if (this.newsForm.value.releaseDate && this.newsForm.value.releaseTime) {
        this.inProgressText = 'Programando';
        this.saveButtonText = 'Programar';
      } else {
        this.inProgressText = 'Criando';
        this.saveButtonText = 'Criar';
      }
    }
  }

  handleSaveButtonClicked() {
    this.saveInProgress = true;
    this.saveNews();
  }

  private releaseTimeIsGreaterThanNow(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let d = this.buildReleaseDate();
      let err = null;

      if (
        d &&
        !isNaN(d.getTime()) &&
        isToday(d) &&
        d.getTime() < new Date().getTime()
      ) {
        err = {
          time: 'The release time must be greater than the current one',
          dates: null,
        };
      }
      control.get('releaseTime')?.setErrors(err);
      control.get('releaseDate')?.setErrors(err);
      return err;
    };
  }

  private validateAuthorizationLimitTime(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let authorizationLimitDate = this.buildAuthorizationLimitDate();
      let err = null;

      if (authorizationLimitDate) {
        if (
          !isNaN(authorizationLimitDate.getTime()) &&
          isToday(authorizationLimitDate) &&
          authorizationLimitDate.getTime() < new Date().getTime()
        ) {
          err = { msg: 'Limite da autorização deve ser em hora futura' };
        }
        let releaseDate = this.buildReleaseDate();
        if (
          releaseDate &&
          !isNaN(releaseDate.getTime()) &&
          authorizationLimitDate.getTime() <= releaseDate.getTime()
        ) {
          err = {
            msg: 'Prazo deve ser posterior à publicação',
          };
        }
      }
      control.get('authorizationLimitTime')?.setErrors(err);
      control.get('authorizationLimitDate')?.setErrors(err);
      return err;
    };
  }

  editNewsReleased(newsId: string | undefined) {
    if (newsId) {
      if (this.news?.releaseDate) {
        let date = this.buildReleaseDate();
        if (date) {
          if (date.getTime() < new Date().getTime()) {
            this.newsForm.controls['releaseDate'].disable();
            this.newsForm.controls['releaseTime'].disable();
          } else {
            this.newsForm.controls['releaseDate'].enable();
            this.newsForm.controls['releaseTime'].enable();
          }
        }
      }
    }
  }

  onAuthorizationChange() {
    this.isAuthorizationOn = !this.isAuthorizationOn;
    if (!this.isAuthorizationOn) {
      this.newsForm.controls['authorizationLimitDate'].reset();
      this.newsForm.controls['authorizationLimitTime'].reset();
      this.newsForm.markAsDirty();
    }
  }

  //#region editor - eligible for refactoring

  // bindings for editor
  addBindingCreated(quill: any) {
    quill.keyboard.addBinding(
      {
        key: '7',
        shiftKey: true,
        shortKey: true,
      },
      (range: any, context: any) => {
        if (context.format.list === 'ordered') {
          quill.formatLine(range.index, 1, 'list', false);
        } else {
          quill.formatLine(range.index, 1, 'list', 'ordered');
        }
      }
    );
    quill.keyboard.addBinding(
      {
        key: '8',
        shiftKey: true,
        shortKey: true,
      },
      (range: any, context: any) => {
        if (context.format.list === 'bullet') {
          quill.formatLine(range.index, 1, 'list', false);
        } else {
          quill.formatLine(range.index, 1, 'list', 'bullet');
        }
      }
    );
  }

  //custom configuration
  customModules = {
    magicUrl: true,
    keyboard: {
      bindings: {
        indent: {
          key: 'tab',
          handler: function () {
            return false;
          },
        },
      },
    },
  };
  //#endregion
}
